'use client';
import { useCallback, useSyncExternalStore } from 'react';

/**
 * A decorative looping mp4 that is effectively the whole page weight (the dashboard hero banner, the login
 * background). Default to the webp poster and only upgrade to the video once we know, client-side, that motion
 * is welcome AND the visitor isn't on Save-Data. So reduced-motion and Save-Data users get a still and never
 * download the clip. The poster is frame 0 of the exact encode, so img → video is a seamless swap.
 *
 * `alt` is a second cut for viewports matching `alt.media` (the login's phone crop): its poster rides a
 * <picture> source and its clip replaces the main one, re-picked if the viewport flips across the query.
 *
 * useSyncExternalStore (not an effect) is what makes the SSR handoff clean: the server + first hydration render
 * both use the server snapshot (poster), then React re-reads the client snapshot and upgrades to the video with
 * no hydration mismatch.
 */
const MOTION_QUERY = '(prefers-reduced-motion: reduce)';

type Cut = { src: string; poster: string };
type Mode = 'poster' | 'main' | 'alt';

// Server + first hydration render never emit the video, so the clip is never in the SSR/hydration payload.
const serverSnapshot = (): Mode => 'poster';

export function PosterVideo({ className, src, poster, alt }: Cut & { className: string; alt?: Cut & { media: string } }) {
  const altMedia = alt?.media;
  const subscribe = useCallback(
    (onChange: () => void) => {
      const queries = altMedia ? [MOTION_QUERY, altMedia] : [MOTION_QUERY];
      const mqs = queries.map((q) => window.matchMedia(q));
      mqs.forEach((mq) => mq.addEventListener('change', onChange));
      return () => mqs.forEach((mq) => mq.removeEventListener('change', onChange));
    },
    [altMedia],
  );
  const snapshot = useCallback((): Mode => {
    const reduce = window.matchMedia(MOTION_QUERY).matches;
    const saveData = (navigator as Navigator & { connection?: { saveData?: boolean } }).connection?.saveData;
    if (reduce || saveData) return 'poster';
    return altMedia && window.matchMedia(altMedia).matches ? 'alt' : 'main';
  }, [altMedia]);
  const mode = useSyncExternalStore(subscribe, snapshot, serverSnapshot);

  if (mode !== 'poster') {
    const cut = mode === 'alt' && alt ? alt : { src, poster };
    return (
      <div className={className} aria-hidden>
        <video key={cut.src} autoPlay muted loop playsInline poster={cut.poster}>
          <source src={cut.src} type="video/mp4" />
        </video>
      </div>
    );
  }
  return (
    <div className={className} aria-hidden>
      {alt ? (
        <picture>
          <source media={alt.media} srcSet={alt.poster} />
          <img src={poster} alt="" />
        </picture>
      ) : (
        // eslint-disable-next-line @next/next/no-img-element
        <img src={poster} alt="" />
      )}
    </div>
  );
}
